import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'components/button';
import MessageText from 'components/message/MessageText';
import Message from 'components/message/Message';
import { Card, CardBody } from 'components/card';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_EDIT_EXERCISES,
  ROUTE_EXERCISES_TEMPLATES_ENTITY,
  ROUTE_EXERCISES_TEMPLATES_ENTITY_TEMPLATE_EXERCISES_EXERCISE
} from 'routes/RouteList';
import Icon from 'components/icon/Icon';
import { useStore } from 'stores/Store';
import TemplateForm from 'containers/pages/exercises/templates/TemplateForm';
import TemplateExercisesListSortable from 'containers/pages/exercises/templates/TemplateExercisesListSortable';
import { sortByArray } from 'utils/sort';
import { TemplateType } from 'constants';
import { useClickAway } from '@uidotdev/usehooks';
import { MODAL_TYPES } from 'models/ModalData';
import { StoreContext } from 'index';
import Template from 'models/Template';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import AddExercisesModal from 'containers/pages/exercises/exercises/AddExercisesModal';
import AddProgramModal from 'containers/pages/exercises/programs/AddProgramModal';
import RemoveExercisesFromTemplateButton from 'containers/partials/buttons/RemoveExercisesFromTemplateButton';
import useTemplate from 'hooks/queries/useTemplate';
import useTemplates from 'hooks/queries/useTemplates';
import RemoveExerciseTemplateButton from 'containers/partials/buttons/RemoveExerciseTemplateButton';
import { useNotificationQueue } from 'components/notification';

const TemplateExercisesTab = () => {
  const { uiState } = useContext(StoreContext);
  const intl = useIntl();
  const { entityId, templateId } = useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const notification = useNotificationQueue();

  const { addActionButton, setMenuItems, clearAllContext } = useContextMenu();

  const templateFormRef = useRef();
  const defaultSort = useRef([]);

  const { fetchTemplate } = useTemplate();
  const { fetchTemplates } = useTemplates(entityId);
  const template = useStore(state => state.template);
  const setTemplate = useStore(state => state.setTemplate);

  const setSelectedExercises = useStore(state => state.setSelectedExercises);
  const selectedExercises = useStore(state => state.selectedExercises);
  const selectExercises = useStore(state => state.selectExercises);
  const resetSelectedExercises = useStore(
    state => state.resetSelectedExercises
  );
  const [addExercisesModal, setAddExercisesModal] = useState(null);
  const [programModalData, setProgramModalData] = useState(null);

  const templateSortableActive = useStore(
    state => state.templateSortableActive
  );
  const setTemplateSortableActive = useStore(
    state => state.setTemplateSortableActive
  );
  const sortableContainerRef = useClickAway(() => {
    if (templateSortableActive) {
      uiState.showModal({
        title: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningTitle
        ),
        message: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningMessage
        ),
        okLabel: intl.formatMessage(messages.saveButton),
        dismissLabel: intl.formatMessage(messages.buttonBack),
        type: MODAL_TYPES.WARNING,
        okHandler: () => {
          templateFormRef.current.submitForm();
        }
      });
    }
  });

  useEffect(() => {
    const contextButtons = [];

    contextButtons.push(<UseTemplateBtn />);

    if (ability.can('update', template)) {
      contextButtons.push(<RemoveExercisesBtn />, <SortExercisesBtn />);
    }

    if (ability.can('delete', template)) {
      contextButtons.push(<DeleteTemplateBtn />);
    }

    setMenuItems(contextButtons);

    if (ability.can('update', template)) {
      addActionButton(<AddExercisesBtn />);
    }

    return () => {
      clearAllContext();
    };
  }, [template, selectedExercises, templateSortableActive]);

  useEffect(() => {
    defaultSort.current = template.exercises.map(exercise => exercise.id);
    setSelectedExercises([]);
    return () => {
      resetSelectedExercises();
    };
  }, [template]);

  const onClickHandler = exerciseId => {
    push(
      generatePath(
        ROUTE_EXERCISES_TEMPLATES_ENTITY_TEMPLATE_EXERCISES_EXERCISE,
        {
          entityId,
          templateId,
          exerciseId
        }
      )
    );
  };

  const onCancel = () => {
    // sortByArray(template.exercises, defaultSort.current);
    setTemplateSortableActive(false);
    setTemplate(
      new Template({
        ...template,
        exercises: sortByArray(template.exercises, defaultSort.current)
      })
    );
  };

  const onAddedHandler = () => {
    setAddExercisesModal(false);
  };

  const onUseHandler = template => {
    // show use template modal
    setProgramModalData({
      entityId,
      title: '',
      startdate: '',
      duration: '',
      description: '',
      exerciseIds: template.exercises.map(e => e.id),
      templateId: template.id
    });
  };

  const onDeletedHandler = async () => {
    notification.add(templateId, {
      message: intl.formatMessage(messages.messageRemovedTemplateSuccess)
    });

    await fetchTemplates({ fetchPolicy: 'network-only' });
    push(
      generatePath(ROUTE_EXERCISES_TEMPLATES_ENTITY, {
        entityId
      })
    );
  };

  const AddExercisesBtn = () => (
    <Button rounded primary onClick={() => setAddExercisesModal(template)}>
      <Icon id="add-white" />
      <FormattedMessage {...messages.exercisesAddExercisesButton} />
    </Button>
  );

  const UseTemplateBtn = () => (
    <Button menu onClick={() => onUseHandler(template)}>
      <FormattedMessage {...messages.exercisesSaveTemplateAsProgramButton} />
      <Icon id="use-template" />
    </Button>
  );

  const RemoveExercisesBtn = () => (
    <RemoveExercisesFromTemplateButton
      disabledMessage={
        templateSortableActive
          ? intl.formatMessage(
              messages.collectionRemoveExerciseDisabledDragging
            )
          : intl.formatMessage(
              messages.collectionRemoveExerciseDisabledNotSelected
            )
      }
      entityId={entityId}
      type="template"
      exerciseIds={selectedExercises.map(e => e.id)}
      templateId={templateId}
      updated={async () => {
        setSelectedExercises([]);
        fetchTemplate({
          variables: { templateId },
          fetchPolicy: 'network-only'
        });
        fetchTemplates({
          variables: { entityId, type: TemplateType.TEMPLATE },
          fetchPolicy: 'network-only'
        });
      }}
      disabled={selectedExercises.length === 0}
    />
  );

  const SortExercisesBtn = () => (
    <Button
      menu
      onClick={() => setTemplateSortableActive(true)}
      disabled={selectedExercises.length > 0 || templateSortableActive}
    >
      {templateSortableActive ? (
        <FormattedMessage
          {...messages.templatesSortExercisesDisabledDragging}
        />
      ) : (
        <FormattedMessage {...messages.sortTemplateButton} />
      )}
      <Icon id="arrange" strokeColor="color-neutral-dark" />
    </Button>
  );

  const DeleteTemplateBtn = () => (
    <RemoveExerciseTemplateButton
      menu
      templateId={template.id}
      collectionType={TemplateType.TEMPLATE}
      entityId={entityId}
      onDeleted={onDeletedHandler}
    >
      <FormattedMessage {...messages.exercisesRemoveTemplateButton} />
    </RemoveExerciseTemplateButton>
  );

  return (
    <>
      <div
        ref={sortableContainerRef}
        className="o-flex o-flex--column u-padding-vertical-small"
        style={{
          // overflowY: 'scroll', // TODO @fabian: deze zorgt voor extra scroll bar
          flex: 1,
          marginBottom: templateSortableActive ? 72 : 0
        }}
      >
        {template.exercises.length > 0 ? (
          <TemplateForm
            ref={templateFormRef}
            entityId={entityId}
            template={template}
            type={TemplateType.TEMPLATE}
            onComplete={() => setTemplateSortableActive(false)}
          >
            <TemplateExercisesListSortable
              exercises={template.exercises}
              checkable={!templateSortableActive}
              showNote={true}
              edit={!templateSortableActive}
              onClick={onClickHandler}
              onCancel={onCancel}
              selectedExercises={selectedExercises}
              onSelectedExercisesChange={selectExercises}
            />
          </TemplateForm>
        ) : (
          <Card centered>
            <CardBody empty>
              <Message emptyState={true} icon="browse">
                <MessageText>
                  <FormattedMessage {...messages.templateEmptyExercises} />
                </MessageText>
                <Button
                  primary
                  rounded
                  onClick={() => setAddExercisesModal(template)}
                >
                  <Icon id="add-white" />
                  <FormattedMessage {...messages.exercisesAddExercisesButton} />
                </Button>
              </Message>
            </CardBody>
          </Card>
        )}
      </div>
      {ability.can('update', template) && addExercisesModal && (
        <TemplateForm
          entityId={entityId}
          template={template}
          type={TemplateType.TEMPLATE}
          onComplete={() => setAddExercisesModal(false)}
        >
          {() => (
            <AddExercisesModal
              entityId={entityId}
              template={template}
              onClose={() => {
                setAddExercisesModal(false);
              }}
              onAdded={() => onAddedHandler()}
            />
          )}
        </TemplateForm>
      )}
      {programModalData && (
        <AddProgramModal
          entityId={entityId}
          program={programModalData}
          destiny={{
            path: ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_EDIT_EXERCISES,
            variables: {}
          }}
          onClose={() => setProgramModalData(null)}
          fromTemplate
        />
      )}
    </>
  );
};

export default TemplateExercisesTab;
